import { DeleteStages } from 'constants/deleteStages';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { useNotify, useRedirect, useShowController } from 'react-admin';
import { CancelButton, DeleteButton, EditLinkButton } from 'shared/mui/Buttons';
import { ContextMenu, ContextMenuEntityUserActionsButton } from 'shared/mui/ContextMenu';
import { ContextMenuUserEntityPermissionsDialogButton } from 'shared/mui/ContextMenu/ContextMenuUserEntityPermissionsDialogButton/ContextMenuUserEntityPermissionsDialogButton';
import { DeleteStageDialog } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialog';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { ShowPage } from 'shared/react-admin/Pages/ShowPage/ShowPage';
import { GatewaysShowChipFields } from 'shared/widgets/GatewayShow/GatewayShowChipFields/GatewayShowChipFields';
import { GatewaysShowSkeleton } from 'shared/widgets/GatewayShow/GatewaysShowSkeleton/GatewaysShowSkeleton';
import { GatewaysShowTable } from 'shared/widgets/GatewayShow/GatewaysShowTable/GatewaysShowTable';
import { UserEntityPermissionsDialog } from 'shared/widgets/UserEntityPermissionsDialog/UserEntityPermissionsDialog';
import { Gateway } from 'types/embedded/gateway';
import { Permission } from 'types/embedded/permission';
import { UserEntityPermissionsNames } from 'types/embedded/userEntityPermission';

export const GatewaysShow = () => {
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const { record } = useShowController<Gateway>();
  const { handleCloseRecordModal, openRecordModal, handleOpenRecordModal } =
    useRecordModal<Gateway>();
  const {
    handleCloseRecordModal: handleCloseRecordModalPermissions,
    openRecordModal: openRecordModalPermissions,
    handleOpenRecordModal: handleOpenRecordModalPermissions,
  } = useRecordModal<Gateway>();

  const redirect = useRedirect();
  const notify = useNotify();
  const { deleteOneRecord, denyDeleteReason, stage, setStage } = useStagedDelete({
    record,
    onSuccess: () => {
      handleCloseRecordModal();
      setStage(DeleteStages.Initial);
      redirect('list', Resources.Gateways);
      notify('Шлюз успешно удален!', { type: 'success' });
    },
  });

  const handleCloseDeleteDialog = () => {
    setStage(DeleteStages.Initial);
    handleCloseRecordModal();
  };

  const getActions = (): React.ReactElement | undefined => {
    const actions = [];
    if (hasResourceAccess(Resources.UserActions)) {
      actions.push(
        <ContextMenuEntityUserActionsButton entityName="gateway" key="user-action-link" />,
      );
    }
    if (hasResourceAccess('user_entity_permissions')) {
      actions.push(
        <ContextMenuUserEntityPermissionsDialogButton
          onClick={() => handleOpenRecordModalPermissions(record)}
        />,
      );
    }
    return actions.length ? <ContextMenu>{actions}</ContextMenu> : undefined;
  };

  return (
    <ShowPage
      actions={getActions()}
      listName="Шлюзы"
      rightActionsSlot={[
        hasResourcePermissions(Resources.Gateways, [Permission.Full, Permission.Delete]) && (
          <DeleteButton
            key="delete-button"
            onClick={() => handleOpenRecordModal(record)}
            variant="outlined"
          />
        ),
        hasResourcePermissions(Resources.Gateways, [Permission.Full, Permission.Update]) &&
          !record?.locked && (
            <EditLinkButton key="edit-button" label="Редактировать" variant="contained" />
          ),
      ]}
      skeleton={<GatewaysShowSkeleton labelWidth="233px" />}
      title="Просмотр шлюза"
    >
      <GatewaysShowChipFields />
      <GatewaysShowTable />
      {record && openRecordModalPermissions && (
        <UserEntityPermissionsDialog
          entityName={UserEntityPermissionsNames.Gateway}
          onClose={handleCloseRecordModalPermissions}
          open={openRecordModalPermissions}
          record={record}
          titleCreate="Включение доступа к шлюзу"
          titleEdit="Редактирование доступа к шлюзу"
        />
      )}
      <DeleteStageDialog
        deleteOneRecord={deleteOneRecord}
        details={
          <Typography>
            Шлюз <TypographyBold fontSize={16} text={record?.name || ''} /> будет удален без
            возможности восстановления.
          </Typography>
        }
        onClose={handleCloseDeleteDialog}
        open={openRecordModal}
        stage={stage}
        title="Удаление шлюза"
      >
        <DeleteStageDialogDenyReason reason={denyDeleteReason}>
          <CancelButton
            key="history-back-button"
            label="Вернуться к списку шлюзов"
            onClick={handleCloseDeleteDialog}
            variant="outlined"
          />
        </DeleteStageDialogDenyReason>
      </DeleteStageDialog>
    </ShowPage>
  );
};
